import React from "react";
import type { VariantStock } from "../StockUpdateModal";

interface DesktopStockUpdateViewProps {
  variants: VariantStock[];
  onStockChange: (
    variantId: string,
    warehouseId: string,
    newStock: number
  ) => void;
}

const DesktopStockUpdateView: React.FC<DesktopStockUpdateViewProps> = ({
  variants,
  onStockChange,
}) => {
  const handleStockInputChange = (
    variantId: string,
    warehouseId: string,
    value: string
  ) => {
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onStockChange(variantId, warehouseId, numValue);
    } else if (value === "") {
      onStockChange(variantId, warehouseId, 0);
    }
  };

  return (
    <>
      <div className="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-lg">
        <table
          className="tw-min-w-full tw-divide-y tw-divide-gray-200 tw-text-sm"
          data-testid="stock-table"
        >
          <thead className="tw-bg-gray-50" data-testid="stock-table-header">
            <tr>
              <th
                className="tw-px-4 tw-py-2.5 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[45%]"
                scope="col"
              >
                Product / Variant
              </th>
              <th
                className="tw-px-4 tw-py-2.5 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider"
                scope="col"
              >
                Warehouse
              </th>
              <th
                className="tw-px-4 tw-py-2.5 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-36"
                scope="col"
              >
                Current Stock
              </th>
              <th
                className="tw-px-4 tw-py-2.5 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-36"
                scope="col"
              >
                Ready Stock
              </th>
            </tr>
          </thead>
          <tbody
            className="tw-divide-y tw-divide-gray-200 tw-bg-white"
            data-testid="stock-table-body"
          >
            {variants.map((variant, variantIndex) =>
              variant.warehouses.map((warehouse, warehouseIndex) => (
                <tr
                  key={`${variant.variantId}-${warehouse.warehouseId}`}
                  data-testid={`stock-row-${variantIndex}-${warehouseIndex}`}
                >
                  <td
                    className="tw-px-4 tw-py-3 tw-align-middle"
                    data-testid={`variant-${variantIndex}-${warehouseIndex}`}
                  >
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <img
                        alt="Product image"
                        className="tw-h-12 tw-w-12 tw-rounded-md tw-object-cover"
                        src={
                          variant.image ||
                          "https://via.placeholder.com/48x48?text=No+Image"
                        }
                        data-testid={`stock-variant-image-${variantIndex}`}
                      />
                      <div className="tw-flex tw-flex-col">
                        <p
                          className="tw-font-bold tw-text-gray-800"
                          data-testid={`stock-variant-name-${variantIndex}`}
                        >
                          {variant.variantName}
                        </p>
                        {variant.sku && variant.variantName !== variant.sku && (
                          <p
                            className="tw-text-xs tw-text-gray-500 tw-mt-0.5"
                            data-testid={`stock-variant-sku-${variantIndex}`}
                          >
                            Variant: {variant.sku}
                          </p>
                        )}
                        <div
                          className="tw-text-xs tw-text-gray-400 tw-mt-1 tw-space-x-2"
                          data-testid={`stock-variant-skus-${variantIndex}`}
                        >
                          {variant.merchantSku && (
                            <span>M.SKU: {variant.merchantSku}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td
                    className="tw-px-4 tw-py-3 tw-align-middle tw-text-gray-800"
                    title={warehouse.warehouseName}
                    data-testid={`warehouse-name-${variantIndex}-${warehouseIndex}`}
                  >
                    {warehouse.warehouseName}
                  </td>
                  <td
                    className="tw-px-4 tw-py-3 tw-align-middle tw-text-gray-600"
                    data-testid={`current-stock-${variantIndex}-${warehouseIndex}`}
                  >
                    {warehouse.currentStock}
                  </td>
                  <td className="tw-px-4 tw-py-3 tw-align-middle">
                    <input
                      type="number"
                      min="0"
                      value={warehouse.readyStock}
                      onChange={(e) =>
                        handleStockInputChange(
                          variant.variantId,
                          warehouse.warehouseId,
                          e.target.value
                        )
                      }
                      className="form-input tw-w-28 tw-rounded-md tw-border-gray-300 tw-shadow-sm focus:tw-border-primary focus:tw-ring-primary tw-text-sm tw-p-2"
                      placeholder={warehouse.currentStock.toString()}
                      data-testid={`ready-stock-input-${variantIndex}-${warehouseIndex}`}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p className="tw-text-xs tw-text-gray-500 tw-text-center tw-mt-4">
        Warehouses are fixed per marketplace. Adjust only stock quantities. All
        updates sync automatically.
      </p>
    </>
  );
};

export default DesktopStockUpdateView;
